import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  DollarSign, TrendingUp, TrendingDown, ArrowRight,
  ArrowUpRight, Calendar, Download, Filter, ChevronDown,
  PieChart, BarChart2, LineChart as LineChartIcon,
  Users, Building2, Wallet, Percent, Clock, Search
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, BarChart, Bar,
  PieChart as RePieChart, Pie, Cell
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import SharedFooter3 from "../../Footer/SharedFooter3";
import { getAdminRevenueStats } from "../../redux/Actions/AdminAction";

const RevenuePage = () => {
  // State management
  const [dateRange, setDateRange] = useState("month");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState("gross");
  const [chartView, setChartView] = useState("line");
  const [isLoadingData, setIsLoadingData] = useState(false);
  
  // Redux state and dispatch
  const dispatch = useDispatch();
  const { revenueStats } = useSelector((state: any) => state.admin);

  // Fetch revenue data on component mount and when date range changes
  useEffect(() => {
    setIsLoadingData(true);
    dispatch(getAdminRevenueStats({ isShowLoader: false, period: dateRange }) as any)
      .finally(() => setIsLoadingData(false));
  }, [dispatch, dateRange]);

  // Use real API data or fallback to empty array
  const monthlyRevenue = revenueStats?.monthlyData || [];

  // Revenue breakdown data - Use real data from API
  const revenueBreakdown = revenueStats?.revenueBreakdown || [];

  // Revenue by service type - Use real data from API
  const revenueByService = revenueStats?.topServices || [];

  // Recent revenue activities - Use real data from API
  const recentActivities = revenueStats?.recentActivities || [];

  // Use API response totals or calculate from monthly data
  const totalRevenue = revenueStats?.totalRevenue || (monthlyRevenue.length > 0 ? monthlyRevenue.reduce((acc, curr) => acc + curr.revenue, 0) : 0);
  const totalTransactions = revenueStats?.totalTransactions || (monthlyRevenue.length > 0 ? monthlyRevenue.reduce((acc, curr) => acc + curr.transactions, 0) : 0);
  const totalPlatformFees = revenueStats?.totalPlatformFees || (monthlyRevenue.length > 0 ? monthlyRevenue.reduce((acc, curr) => acc + curr.platformFees, 0) : 0);
  const averageTransactionValue = totalTransactions > 0 ? totalRevenue / totalTransactions : 0;
  
  // Get period labels based on date range
  const getPeriodLabel = (dateRange: string) => {
    switch (dateRange) {
      case "week": return "vs. last week";
      case "month": return "vs. last month";
      case "quarter": return "vs. last quarter";
      case "year": return "vs. last year";
      default: return "vs. last period";
    }
  };

  // Calculate period-over-period changes based on date range
  const getPeriodChange = (currentValue: number, previousValue: number) => {
    if (previousValue === 0) {
      // If previous value is 0, any current value is considered positive growth
      return currentValue > 0 ? { percentage: 100, isPositive: true } : { percentage: 0, isPositive: true };
    }
    const percentage = ((currentValue - previousValue) / previousValue) * 100;
    return { percentage: Math.abs(percentage), isPositive: percentage >= 0 };
  };

  // Get current period vs previous period based on date range
  const getCurrentAndPreviousPeriods = () => {
    if (monthlyRevenue.length === 0) return { current: null, previous: null };
    
    // For all date ranges, compare last period vs second-to-last period
    const current = monthlyRevenue[monthlyRevenue.length - 1];
    const previous = monthlyRevenue.length > 1 ? monthlyRevenue[monthlyRevenue.length - 2] : null;
    
    return { current, previous };
  };

  const { current: currentPeriod, previous: previousPeriod } = getCurrentAndPreviousPeriods();

  // Calculate growth metrics based on selected period
  const revenueGrowth = currentPeriod && previousPeriod 
    ? getPeriodChange(currentPeriod.revenue, previousPeriod.revenue)
    : { percentage: 0, isPositive: true };
  
  const transactionGrowth = currentPeriod && previousPeriod 
    ? getPeriodChange(currentPeriod.transactions, previousPeriod.transactions)
    : { percentage: 0, isPositive: true };
  
  const avgTransactionGrowth = currentPeriod && previousPeriod 
    ? getPeriodChange(
        currentPeriod.transactions > 0 ? currentPeriod.revenue / currentPeriod.transactions : 0,
        previousPeriod.transactions > 0 ? previousPeriod.revenue / previousPeriod.transactions : 0
      )
    : { percentage: 0, isPositive: true };
  
  const platformFeesGrowth = currentPeriod && previousPeriod 
    ? getPeriodChange(currentPeriod.platformFees, previousPeriod.platformFees)
    : { percentage: 0, isPositive: true };

  // Revenue Overview Section
  const RevenueOverview = () => (
    <Card className="p-6"  style={{ textAlign: "left" }}>
      <div className="flex justify-between items-start mb-6">
        <h2 className="text-lg font-semibold">Revenue Overview</h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setChartView("line")}
            className={chartView === "line" ? "bg-blue-50" : ""}
          >
            <LineChartIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setChartView("bar")}
            className={chartView === "bar" ? "bg-blue-50" : ""}
          >
            <BarChart2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      <div className="h-[300px]"  style={{ textAlign: "left" }}>
        <ResponsiveContainer width="100%" height="100%">
          {chartView === "line" ? (
            <LineChart data={monthlyRevenue}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line
                type="monotone"
                dataKey={selectedMetric === "gross" ? "revenue" : "netRevenue"}
                stroke="#4F46E5"
                strokeWidth={2}
              />
            </LineChart>
          ) : (
            <BarChart data={monthlyRevenue}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Bar
                dataKey={selectedMetric === "gross" ? "revenue" : "netRevenue"}
                fill="#4F46E5"
              />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </Card>
  );

  // Revenue Breakdown Chart
  const RevenueBreakdownChart = () => (
    <Card className="p-6"  style={{ textAlign: "left" }}>
      <h2 className="text-lg font-semibold mb-6">Revenue Distribution</h2>
      {revenueBreakdown.length > 0 ? (
        <>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <RePieChart>
                <Pie
                  data={revenueBreakdown}
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {revenueBreakdown.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </RePieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-6 space-y-2">
            {revenueBreakdown.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-sm text-gray-600">{item.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{item.value}%</span>
                  <span className="text-xs text-gray-500">(${item.revenue.toLocaleString()})</span>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <p>No revenue distribution data available</p>
        </div>
      )}
    </Card>
  );

  // Top Services by Revenue
  const TopServices = () => (
    <Card className="p-6" style={{ textAlign: "left" }}>
      <h2 className="text-lg font-semibold mb-6">Top Services by Revenue</h2>
      {revenueByService.length > 0 ? (
        <div className="space-y-4">
          {revenueByService.map((service, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <span className="text-blue-600 font-medium">{index + 1}</span>
                </div>
                <div>
                  <span className="font-medium">{service.service}</span>
                  <p className="text-xs text-gray-500">{service.transactions} transactions</p>
                </div>
              </div>
              <span className="text-gray-600 font-semibold">${service.revenue.toLocaleString()}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <p>No service data available for the selected period</p>
        </div>
      )}
    </Card>
  );

  // Recent Activities
  const RecentActivities = () => (
    <Card className="p-6"  style={{ textAlign: "left" }}>
      <h2 className="text-lg font-semibold mb-6">Recent Activities</h2>
      {recentActivities.length > 0 ? (
        <div className="space-y-4">
          {recentActivities.map((activity) => (
            <div key={activity.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-4">
                <div className={`p-2 rounded-lg ${
                  activity.type === "payment" ? "bg-green-100" : "bg-red-100"
                }`}>
                  <DollarSign className={`h-5 w-5 ${
                    activity.type === "payment" ? "text-green-600" : "text-red-600"
                  }`} />
                </div>
                <div>
                  <p className="font-medium">{activity.service}</p>
                  <p className="text-sm text-gray-500">{activity.provider}</p>
                </div>
              </div>
              <div className="text-right">
                <p className={`font-medium ${
                  activity.type === "payment" ? "text-green-600" : "text-red-600"
                }`}>
                  {activity.type === "payment" ? "+" : "-"}${activity.amount.toFixed(2)}
                </p>
                <p className="text-sm text-gray-500">{activity.time}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <p>No recent activities available</p>
        </div>
      )}
    </Card>
  );

  // Show loading state if no data yet or loading
  if (isLoadingData || !revenueStats || !revenueStats.monthlyData || revenueStats.monthlyData.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
        <AdminHeader />
        <main className="flex-1 lg:pl-64 pt-16">
          <div className="p-6">
            <div className="max-w-7xl mx-auto space-y-6">
              <div className="flex justify-center items-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">
                    {isLoadingData ? `Loading ${dateRange} revenue data...` : "Loading revenue data..."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50"  style={{ textAlign: "left" }}>
      <AdminHeader />
      
      <main className="flex-1 lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Revenue</h1>
                <p className="text-gray-600 mt-1">Track and analyze your revenue streams</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Export Report
                </Button>
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="px-4 py-2 border rounded-lg"
                >
                  <option value="day">Last 24 Hours</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="quarter">Last Quarter</option>
                  <option value="year">Last Year</option>
                </select>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Total Revenue</p>
                    <p className="text-2xl font-semibold">${totalRevenue.toLocaleString()}</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-green-600" />
                  </div>
                </div>
                <div className="mt-4 flex items-center text-sm">
                  {revenueGrowth.isPositive ? (
                    <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
                  )}
                  <span className={`font-medium ${revenueGrowth.isPositive ? "text-green-500" : "text-red-500"}`}>
                    {revenueGrowth.isPositive ? "+" : "-"}{revenueGrowth.percentage.toFixed(1)}%
                  </span>
                  <span className="text-gray-500 ml-1">{getPeriodLabel(dateRange)}</span>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Transactions</p>
                    <p className="text-2xl font-semibold">{totalTransactions.toLocaleString()}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Wallet className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
                <div className="mt-4 flex items-center text-sm">
                  {transactionGrowth.isPositive ? (
                    <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
                  )}
                  <span className={`font-medium ${transactionGrowth.isPositive ? "text-green-500" : "text-red-500"}`}>
                    {transactionGrowth.isPositive ? "+" : "-"}{transactionGrowth.percentage.toFixed(1)}%
                  </span>
                  <span className="text-gray-500 ml-1">{getPeriodLabel(dateRange)}</span>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Avg. Transaction</p>
                    <p className="text-2xl font-semibold">${averageTransactionValue.toFixed(2)}</p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <ArrowUpRight className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
                <div className="mt-4 flex items-center text-sm">
                  {avgTransactionGrowth.isPositive ? (
                    <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
                  )}
                  <span className={`font-medium ${avgTransactionGrowth.isPositive ? "text-green-500" : "text-red-500"}`}>
                    {avgTransactionGrowth.isPositive ? "+" : "-"}{avgTransactionGrowth.percentage.toFixed(1)}%
                  </span>
                  <span className="text-gray-500 ml-1">{getPeriodLabel(dateRange)}</span>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Platform Fees</p>
                    <p className="text-2xl font-semibold">${totalPlatformFees.toLocaleString()}</p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Percent className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
                <div className="mt-4 flex items-center text-sm">
                  {platformFeesGrowth.isPositive ? (
                    <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
                  )}
                  <span className={`font-medium ${platformFeesGrowth.isPositive ? "text-green-500" : "text-red-500"}`}>
                    {platformFeesGrowth.isPositive ? "+" : "-"}{platformFeesGrowth.percentage.toFixed(1)}%
                  </span>
                  <span className="text-gray-500 ml-1">{getPeriodLabel(dateRange)}</span>
                </div>
              </Card>
            </div>

            {/* Filters */}
            <div className="flex gap-4 items-center">
              <Button
                variant={selectedMetric === "gross" ? "default" : "outline"}
                onClick={() => setSelectedMetric("gross")}
              >
                Gross Revenue
              </Button>
              <Button
                variant={selectedMetric === "net" ? "default" : "outline"}
                onClick={() => setSelectedMetric("net")}
              >
                Net Revenue
              </Button>
            </div>

            {/* Charts Grid */}
            <div className="grid lg:grid-cols-2 gap-6">
              <RevenueOverview />
              <RevenueBreakdownChart />
            </div>

            {/* Services and Activities Grid */}
            <div className="grid lg:grid-cols-2 gap-6">
              <TopServices />
              <RecentActivities />
            </div>

            {/* Revenue Trends */}
            <Card className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-lg font-semibold">Revenue Trends</h2>
                  <p className="text-sm text-gray-500 mt-1">Monthly revenue growth and patterns</p>
                </div>
                <Button variant="outline" className="flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  Filter
                </Button>
              </div>

              <div className="relative overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs text-gray-500 uppercase bg-gray-50">
                    <tr>
                      <th className="px-6 py-3">Month</th>
                      <th className="px-6 py-3">Revenue</th>
                      <th className="px-6 py-3">Transactions</th>
                      <th className="px-6 py-3">Platform Fees</th>
                      <th className="px-6 py-3">Net Revenue</th>
                      <th className="px-6 py-3">Growth</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyRevenue.map((month, index) => {
                      const growth:any = index > 0
                        ? ((month.revenue - monthlyRevenue[index - 1].revenue) / monthlyRevenue[index - 1].revenue * 100).toFixed(1)
                        : 0;
                      
                      return (
                        <tr key={month.month} className="border-b">
                          <td className="px-6 py-4 font-medium">{month.month}</td>
                          <td className="px-6 py-4">${month.revenue.toLocaleString()}</td>
                          <td className="px-6 py-4">{month.transactions}</td>
                          <td className="px-6 py-4">${month.platformFees.toLocaleString()}</td>
                          <td className="px-6 py-4">${month.netRevenue.toLocaleString()}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              {growth > 0 ? (
                                <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                              ) : growth < 0 ? (
                                <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
                              ) : (
                                <ArrowRight className="h-4 w-4 text-gray-500 mr-1" />
                              )}
                              <span className={`${
                                growth > 0 ? "text-green-500" :
                                growth < 0 ? "text-red-500" :
                                "text-gray-500"
                              }`}>
                                {growth}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </div>
      </main>
      <br></br>
      <br></br>
      <br></br>
      <br></br>

      {/* <SharedFooter3 /> */}
    </div>
  );
};

export default RevenuePage;